import { useState, useEffect } from 'react';
import { useBranch } from '../contexts/BranchContext';
import { useConfirm } from '../contexts/ConfirmContext';
import Modal from '../components/Modal';
import Toast from '../components/Toast';

export default function Promotions({ apiUrl }) {
  const { branches } = useBranch();
  const confirm = useConfirm();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text } — แทน alert() เดิม
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [showTranslations, setShowTranslations] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    nameZh: '',
    nameKm: '',
    description: '',
    descriptionEn: '',
    descriptionZh: '',
    descriptionKm: '',
    type: 'percentage',
    value: 0,
    startTime: '11:00',
    endTime: '15:00',
    daysOfWeek: [1, 2, 3, 4, 5], // Mon-Fri
    targetCustomers: ['adult', 'childBig', 'childSmall'],
    isActive: true,
    priority: 1,
    branchIds: [] // ว่าง = ทุกสาขา (chain-wide), มีค่า = จำกัดเฉพาะสาขาที่เลือก
  });

  const daysMap = {
    0: 'อา',
    1: 'จ',
    2: 'อ',
    3: 'พ',
    4: 'พฤ',
    5: 'ศ',
    6: 'ส'
  };

  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/promotions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      setPromotions(result.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load promotions:', err);
      setError('ไม่สามารถโหลดโปรโมชั่นได้');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem('adminToken');
    const url = editingPromotion
      ? `${apiUrl}/api/promotions/${editingPromotion._id}`
      : `${apiUrl}/api/promotions`;
    const method = editingPromotion ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          branchIds: formData.branchIds.length > 0 ? formData.branchIds : null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'เกิดข้อผิดพลาด');
      }

      setMessage({ type: 'success', text: editingPromotion ? 'แก้ไขโปรโมชั่นสำเร็จ' : 'สร้างโปรโมชั่นสำเร็จ' });
      setShowModal(false);
      resetForm();
      await loadPromotions();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleEdit = (promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      name: promotion.name,
      nameEn: promotion.nameEn || '',
      nameZh: promotion.nameZh || '',
      nameKm: promotion.nameKm || '',
      description: promotion.description || '',
      descriptionEn: promotion.descriptionEn || '',
      descriptionZh: promotion.descriptionZh || '',
      descriptionKm: promotion.descriptionKm || '',
      type: promotion.type,
      value: promotion.value,
      startTime: promotion.startTime || '11:00',
      endTime: promotion.endTime || '15:00',
      daysOfWeek: promotion.daysOfWeek || [],
      targetCustomers: promotion.targetCustomers || ['adult', 'childBig', 'childSmall'],
      isActive: promotion.isActive,
      priority: promotion.priority || 1,
      branchIds: (promotion.branchIds || []).map(id => String(id))
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!await confirm('คุณต้องการลบโปรโมชั่นนี้หรือไม่?', { title: 'ลบโปรโมชั่น', confirmText: 'ลบ', danger: true })) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${apiUrl}/api/promotions/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('ไม่สามารถลบโปรโมชั่นได้');

      setMessage({ type: 'success', text: 'ลบโปรโมชั่นสำเร็จ' });
      await loadPromotions();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const [togglingIds, setTogglingIds] = useState(new Set());

  const handleToggleActive = async (id, isActive) => {
    if (togglingIds.has(id)) return;
    setTogglingIds(prev => new Set(prev).add(id));

    setPromotions(prev =>
      prev.map(promo =>
        promo._id === id ? { ...promo, isActive } : promo
      )
    );

    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${apiUrl}/api/promotions/${id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isActive })
      });

      if (!response.ok) {
        setPromotions(prev =>
          prev.map(promo =>
            promo._id === id ? { ...promo, isActive: !isActive } : promo
          )
        );
        throw new Error('ไม่สามารถเปลี่ยนสถานะได้');
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setTogglingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const resetForm = () => {
    setEditingPromotion(null);
    setFormData({
      name: '',
      nameEn: '',
      nameZh: '',
      nameKm: '',
      description: '',
      descriptionEn: '',
      descriptionZh: '',
      descriptionKm: '',
      type: 'percentage',
      value: 0,
      startTime: '11:00',
      endTime: '15:00',
      daysOfWeek: [1, 2, 3, 4, 5],
      targetCustomers: ['adult', 'childBig', 'childSmall'],
      isActive: true,
      priority: 1,
      branchIds: []
    });
  };

  const toggleDay = (day) => {
    const newDays = formData.daysOfWeek.includes(day)
      ? formData.daysOfWeek.filter(d => d !== day)
      : [...formData.daysOfWeek, day].sort((a, b) => a - b);
    setFormData({ ...formData, daysOfWeek: newDays });
  };

  const toggleTarget = (target) => {
    const newTargets = formData.targetCustomers.includes(target)
      ? formData.targetCustomers.filter(t => t !== target)
      : [...formData.targetCustomers, target];
    setFormData({ ...formData, targetCustomers: newTargets });
  };

  const toggleBranch = (branchId) => {
    const newIds = formData.branchIds.includes(branchId)
      ? formData.branchIds.filter(id => id !== branchId)
      : [...formData.branchIds, branchId];
    setFormData({ ...formData, branchIds: newIds });
  };

  if (loading) return <div className="loading">กำลังโหลด...</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: 'var(--text)' }}>🎉 จัดการโปรโมชั่น</h2>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="button"
        >
          + สร้างโปรโมชั่น
        </button>
      </div>

      {error && <div style={{ padding: '12px', background: '#2a1517', border: '1px solid #8a2f33', color: 'var(--danger)', borderRadius: '8px', marginBottom: '20px' }}>{error}</div>}

      <Toast message={message} onDismiss={() => setMessage(null)} />

      {/* ตารางโปรโมชั่น */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
        <table className="table-dark table-nowrap" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--surface-2)', borderBottom: '2px solid var(--border)' }}>
              <th style={{ padding: '16px 20px', textAlign: 'left', background: 'var(--surface-2)', color: 'var(--muted)', fontWeight: '700', fontSize: '14px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>ชื่อโปรโมชั่น</th>
              <th style={{ padding: '16px 20px', textAlign: 'center', background: 'var(--surface-2)', color: 'var(--muted)', fontWeight: '700', fontSize: '14px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>ส่วนลด</th>
              <th style={{ padding: '16px 20px', textAlign: 'center', background: 'var(--surface-2)', color: 'var(--muted)', fontWeight: '700', fontSize: '14px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>เวลา</th>
              <th style={{ padding: '16px 20px', textAlign: 'center', background: 'var(--surface-2)', color: 'var(--muted)', fontWeight: '700', fontSize: '14px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>วัน</th>
              <th style={{ padding: '16px 20px', textAlign: 'center', background: 'var(--surface-2)', color: 'var(--muted)', fontWeight: '700', fontSize: '14px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>สาขา</th>
              <th style={{ padding: '16px 20px', textAlign: 'center', background: 'var(--surface-2)', color: 'var(--muted)', fontWeight: '700', fontSize: '14px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>สถานะ</th>
              <th style={{ padding: '16px 20px', textAlign: 'center', background: 'var(--surface-2)', color: 'var(--muted)', fontWeight: '700', fontSize: '14px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {promotions.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>
                  ยังไม่มีโปรโมชั่น
                </td>
              </tr>
            ) : (
              promotions.map((promo) => (
                <tr
                  key={promo._id}
                  style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ fontWeight: '600', fontSize: '15px', color: 'var(--text)', marginBottom: '2px' }}>{promo.name}</div>
                    {promo.description && (
                      <div style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: '400' }}>{promo.description}</div>
                    )}
                  </td>
                  <td style={{ padding: '15px', textAlign: 'center' }}>
                    <span style={{
                      padding: '8px 16px',
                      background: 'var(--accent)',
                      color: '#1a1206',
                      borderRadius: '8px',
                      fontWeight: '900',
                      fontSize: '16px',
                      letterSpacing: '0.5px'
                    }}>
                      {promo.type === 'percentage' ? `-${promo.value}%` : `-${promo.value}฿`}
                    </span>
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                    <span style={{ color: 'var(--text-2)', fontSize: '14px', fontWeight: '500' }}>{promo.startTime} - {promo.endTime}</span>
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                    <span style={{ color: 'var(--text-2)', fontSize: '14px', fontWeight: '500' }}>{promo.daysOfWeek?.length === 7 ? 'ทุกวัน' : (promo.daysOfWeek || []).map(d => daysMap[d]).join(', ')}</span>
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                    <span style={{ color: 'var(--text-2)', fontSize: '13px', fontWeight: '500' }}>
                      {!promo.branchIds || promo.branchIds.length === 0
                        ? '🌐 ทุกสาขา'
                        : promo.branchIds.map(id => branches.find(b => b.id === String(id))?.name || '-').join(', ')}
                    </span>
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}>
                      <div
                        onClick={() => handleToggleActive(promo._id, !promo.isActive)}
                        style={{
                          position: 'relative',
                          width: '48px',
                          height: '24px',
                          background: promo.isActive ? 'var(--ok)' : 'var(--border-strong)',
                          borderRadius: '9999px',
                          transition: 'background 0.3s',
                          cursor: 'pointer'
                        }}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            top: '2px',
                            left: promo.isActive ? '26px' : '2px',
                            width: '20px',
                            height: '20px',
                            background: '#ffffff',
                            borderRadius: '50%',
                            transition: 'left 0.3s',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.4)'
                          }}
                        />
                      </div>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: promo.isActive ? 'var(--ok)' : 'var(--muted)' }}>
                        {promo.isActive ? 'เปิด' : 'ปิด'}
                      </span>
                    </label>
                  </td>
                  <td style={{ padding: '15px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleEdit(promo)}
                      style={{
                        padding: '6px 12px',
                        background: 'var(--surface-2)',
                        color: 'var(--info)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        marginRight: '8px',
                        fontSize: '13px'
                      }}
                    >
                      แก้ไข
                    </button>
                    <button
                      onClick={() => handleDelete(promo._id)}
                      style={{
                        padding: '6px 12px',
                        background: 'var(--surface-2)',
                        color: 'var(--danger)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      ลบ
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Modal สร้าง/แก้ไขโปรโมชั่น */}
      <Modal open={showModal} onClose={() => { setShowModal(false); resetForm(); }} maxWidth="600px">
            <div style={{
              padding: '0 0 20px 0',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ margin: 0, color: 'var(--text)' }}>
                {editingPromotion ? '✏️ แก้ไขโปรโมชั่น' : '➕ สร้างโปรโมชั่นใหม่'}
              </h3>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--muted)'
                }}
              >×</button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* ชื่อโปรโมชั่น */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>
                  ชื่อโปรโมชั่น *
                </label>
                <input
                  type="text"
                  className="dark-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="เช่น Happy Hour Lunch"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* คำอธิบาย */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>
                  คำอธิบาย
                </label>
                <textarea
                  className="dark-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="ลด 30% ช่วงเที่ยง"
                  rows="2"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
              </div>

              {/* คำแปลภาษาอื่น */}
              <div style={{ marginBottom: '15px' }}>
                <button
                  type="button"
                  onClick={() => setShowTranslations(v => !v)}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    color: 'var(--info)', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline'
                  }}
                >
                  {showTranslations ? '▲ ซ่อนคำแปลภาษาอื่น' : '▼ ตั้งคำแปลภาษาอื่น (อังกฤษ/จีน/เขมร)'}
                </button>
                {showTranslations && (
                  <div style={{ display: 'grid', gap: '10px', marginTop: '10px' }}>
                    {[
                      { nameKey: 'nameEn', descKey: 'descriptionEn', label: 'English' },
                      { nameKey: 'nameZh', descKey: 'descriptionZh', label: '中文' },
                      { nameKey: 'nameKm', descKey: 'descriptionKm', label: 'ខ្មែរ' }
                    ].map(({ nameKey, descKey, label }) => (
                      <div key={nameKey} style={{ padding: '10px', border: '1px solid var(--border)', borderRadius: '8px' }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '6px', fontWeight: 600 }}>{label}</div>
                        <input
                          type="text"
                          className="dark-input"
                          value={formData[nameKey]}
                          onChange={(e) => setFormData({ ...formData, [nameKey]: e.target.value })}
                          placeholder="ชื่อโปรโมชั่น (ว่าง = ใช้ชื่อไทย)"
                          style={{
                            width: '100%',
                            padding: '8px',
                            marginBottom: '6px',
                            border: '1px solid var(--border-strong)',
                            borderRadius: '8px',
                            fontSize: '13px',
                            boxSizing: 'border-box'
                          }}
                        />
                        <textarea
                          className="dark-input"
                          value={formData[descKey]}
                          onChange={(e) => setFormData({ ...formData, [descKey]: e.target.value })}
                          placeholder="คำอธิบาย (ว่าง = ใช้คำอธิบายไทย)"
                          rows="2"
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid var(--border-strong)',
                            borderRadius: '8px',
                            fontSize: '13px',
                            boxSizing: 'border-box',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ประเภทส่วนลด & จำนวน */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>
                    ประเภทส่วนลด *
                  </label>
                  <select
                    className="dark-input"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid var(--border-strong)',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="percentage">เปอร์เซ็นต์ (%)</option>
                    <option value="fixed">บาท (฿)</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>
                    จำนวน *
                  </label>
                  <input
                    type="number"
                    className="dark-input"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: Number(e.target.value) })}
                    required
                    min="0"
                    max={formData.type === 'percentage' ? 100 : undefined}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid var(--border-strong)',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              {/* เวลา */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>
                    เวลาเริ่ม
                  </label>
                  <input
                    type="time"
                    className="dark-input"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid var(--border-strong)',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>
                    เวลาสิ้นสุด
                  </label>
                  <input
                    type="time"
                    className="dark-input"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid var(--border-strong)',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              {/* วันที่ใช้ได้ */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>
                  วันที่ใช้ได้
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[0, 1, 2, 3, 4, 5, 6].map(day => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      style={{
                        padding: '8px 12px',
                        background: formData.daysOfWeek.includes(day) ? 'var(--ok)' : 'var(--surface-2)',
                        color: formData.daysOfWeek.includes(day) ? '#0e2a1c' : 'var(--muted)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}
                    >
                      {daysMap[day]}
                    </button>
                  ))}
                </div>
              </div>

              {/* สาขาที่ใช้ได้ */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>
                  สาขาที่ใช้ได้
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, branchIds: [] })}
                    style={{
                      padding: '8px 16px',
                      background: formData.branchIds.length === 0 ? 'var(--accent)' : 'var(--surface-2)',
                      color: formData.branchIds.length === 0 ? '#1a1206' : 'var(--muted)',
                      border: '1px solid var(--border-strong)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '13px'
                    }}
                  >
                    🌐 ทุกสาขา
                  </button>
                  {branches.map(b => (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggleBranch(b.id)}
                      style={{
                        padding: '8px 16px',
                        background: formData.branchIds.includes(b.id) ? 'var(--accent)' : 'var(--surface-2)',
                        color: formData.branchIds.includes(b.id) ? '#1a1206' : 'var(--muted)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}
                    >
                      {b.name}
                    </button>
                  ))}
                </div>
                <small style={{ color: 'var(--muted)', fontSize: '12px', display: 'block', marginTop: '6px' }}>
                  ไม่เลือกสาขาไหนเลย = ใช้ได้ทุกสาขาในเครือ
                </small>
              </div>

              {/* กลุ่มเป้าหมาย */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>
                  กลุ่มเป้าหมาย
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { value: 'adult', label: '👨 ผู้ใหญ่' },
                    { value: 'childBig', label: '👦 เด็กโต' },
                    { value: 'childSmall', label: '👶 เด็กเล็ก' }
                  ].map(target => (
                    <button
                      key={target.value}
                      type="button"
                      onClick={() => toggleTarget(target.value)}
                      style={{
                        padding: '8px 16px',
                        background: formData.targetCustomers.includes(target.value) ? 'var(--accent)' : 'var(--surface-2)',
                        color: formData.targetCustomers.includes(target.value) ? '#1a1206' : 'var(--muted)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '13px'
                      }}
                    >
                      {target.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ปุ่ม */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  className="button"
                  style={{ flex: 1 }}
                >
                  {editingPromotion ? 'บันทึก' : 'สร้างโปรโมชั่น'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="button-secondary"
                  style={{ padding: '12px 24px', fontSize: '16px' }}
                >
                  ยกเลิก
                </button>
              </div>
            </form>
      </Modal>
    </div>
  );
}
